import { useState, useEffect, useMemo } from 'react';
import { CheckCheck, Trash2, Eye, EyeOff, Loader2, X, SendHorizonal } from 'lucide-react';
import { toast } from 'sonner';
import { notificationService } from '../services/notificationService';
import { warehouseService } from '../services/warehouseService';
import { useAuth } from '../contexts/AuthContext';
import { useAppAlert } from '../components/AppAlertProvider';
import { AdvancedTable } from '../components/AdvancedTable';

export function Notifications() {
  const { user } = useAuth();
  const { showConfirm } = useAppAlert();
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<string>('all');
  const [showRead, setShowRead] = useState(true);
  const [filterRelatedEntity, setFilterRelatedEntity] = useState('all');
  const [replyingNotification, setReplyingNotification] = useState<any | null>(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [replyStatus, setReplyStatus] = useState<'in-progress' | 'resolved'>('in-progress');
  const [sendingReply, setSendingReply] = useState(false);

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      const response = await notificationService.getNotifications({ userId: String(user.id) });
      const items = Array.isArray(response?.data) ? response.data : [];
      setNotifications(items);
    } catch (err) {
      console.error('Failed to load notifications:', err);
      toast.error('Bildirimler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    let items = notifications;
    if (!showRead) items = items.filter((n) => !(n.read ?? n.is_read));
    if (filterType !== 'all') items = items.filter((n) => (n.type || '') === filterType);
    if (filterRelatedEntity !== 'all') items = items.filter((n) => (n.related_entity_type || '') === filterRelatedEntity);
    return items;
  }, [notifications, filterType, showRead, filterRelatedEntity]);

  const handleMarkAllRead = async () => {
    if (!user?.id) return;
    try {
      await notificationService.markAllAsRead(String(user.id));
      setNotifications((current) => current.map((n) => ({ ...n, read: true, is_read: true })));
      toast.success('Tüm bildirimler okundu işaretlendi');
    } catch {
      toast.error('İşlem başarısız');
    }
  };

  const handleDelete = async (id: string | number) => {
    if (!(await showConfirm({ title: 'Bildirimi Sil', message: 'Bu bildirimi silmek istediğinize emin misiniz?', confirmText: 'Sil', tone: 'warning' }))) return;
    try {
      await notificationService.delete(id);
      setNotifications((current) => current.filter((n) => n.id !== id));
      toast.success('Bildirim silindi');
    } catch {
      toast.error('Silinemedi');
    }
  };

  const handleDeleteAll = async () => {
    if (!user?.id) return;
    if (!(await showConfirm({ title: 'Tum Bildirimleri Sil', message: 'Tum bildirimleri silmek istediginize emin misiniz?', confirmText: 'Sil', tone: 'warning' }))) return;
    try {
      await notificationService.deleteAllByUser(String(user.id));
      setNotifications([]);
      toast.success('Tum bildirimler silindi');
    } catch {
      toast.error('Silinemedi');
    }
  };

  const typeLabels: Record<string, string> = {
    system: 'Sistem',
    info: 'Bilgi',
    warning: 'Uyarı',
    error: 'Hata',
    success: 'Başarılı',
  };

  const typeColors: Record<string, string> = {
    system: 'bg-blue-500',
    info: 'bg-blue-500',
    warning: 'bg-orange-500',
    error: 'bg-red-500',
    success: 'bg-green-500',
  };
  const typeRowColors: Record<string, string> = {
    system: 'bg-blue-50/60 hover:!bg-blue-100/70',
    info: 'bg-sky-50/60 hover:!bg-sky-100/70',
    warning: 'bg-amber-50/70 hover:!bg-amber-100/80',
    error: 'bg-rose-50/70 hover:!bg-rose-100/80',
    success: 'bg-emerald-50/70 hover:!bg-emerald-100/80',
  };

  const uniqueTypes = Array.from(new Set(notifications.map((n) => n.type || 'system')));
  const uniqueRelatedEntities = Array.from(new Set(notifications.map((n) => n.related_entity_type).filter(Boolean)));
  const formatLocation = (name?: string, city?: string, district?: string) => {
    return [name, city, district].filter(Boolean).join(' / ') || '-';
  };
  const closeReplyModal = () => {
    if (sendingReply) return;
    setReplyingNotification(null);
    setReplyMessage('');
    setReplyStatus('in-progress');
  };
  const openReplyModal = (row: any) => {
    setReplyingNotification(row);
    setReplyMessage('');
    setReplyStatus('in-progress');
  };
  const handleSendReply = async () => {
    if (!replyingNotification?.related_entity_id) return;
    if (!replyMessage.trim()) {
      toast.error('Yanıt mesajı boş olamaz');
      return;
    }
    try {
      setSendingReply(true);
      await warehouseService.replyWaitingRecord(replyingNotification.related_entity_id, {
        message: replyMessage.trim(),
        ticketStatus: replyStatus,
      });
      toast.success('Yanıt sürücüye gönderildi');
      await loadNotifications();
      closeReplyModal();
    } catch (err: any) {
      console.error('Notification waiting reply failed:', err);
      toast.error(err?.message || 'Yanıt gönderilemedi');
    } finally {
      setSendingReply(false);
    }
  };
  const tableData = useMemo(() => filtered.map((n) => ({
    ...n,
    pickup: formatLocation(n.pickup_name, n.pickup_city, n.pickup_district),
    delivery: formatLocation(n.delivery_name, n.delivery_city, n.delivery_district),
    typeLabel: typeLabels[n.type] || n.type || 'Sistem',
    createdAtText: new Date(n.created_at || n.updated_at || Date.now()).toLocaleString('tr-TR'),
  })), [filtered]);
  const columns: any[] = useMemo(() => [
    { key: 'createdAtText', label: 'Bildirim Tarih Saat', sortable: true, filterable: true, width: '170px' },
    { key: 'title', label: 'Başlık', sortable: true, filterable: true, width: '180px' },
    { key: 'typeLabel', label: 'Tür', sortable: true, filterable: true, width: '120px' },
    { key: 'related_entity_type', label: 'Alt Tür', sortable: true, filterable: true, width: '120px' },
    { key: 'driver_name', label: 'Sürücü', sortable: true, filterable: true, width: '180px' },
    { key: 'shipment_no', label: 'Sevkiyat No', sortable: true, filterable: true, width: '130px' },
    { key: 'order_code', label: 'Sipariş Kodu', sortable: true, filterable: true, width: '140px' },
    { key: 'plan_code', label: 'Plan Kodu', sortable: true, filterable: true, width: '130px' },
    { key: 'load_type', label: 'Ürün Türü', sortable: true, filterable: true, width: '140px' },
    { key: 'cekici_plaka', label: 'Çekici', sortable: true, filterable: true, width: '120px' },
    { key: 'dorse_plaka', label: 'Dorse', sortable: true, filterable: true, width: '120px' },
    { key: 'pickup', label: 'Yükleme', sortable: true, filterable: true, width: '220px' },
    { key: 'delivery', label: 'Boşaltma', sortable: true, filterable: true, width: '220px' },
    { key: 'message', label: 'Mesaj', sortable: true, filterable: true, width: '320px' },
    {
      key: 'id',
      label: 'İşlem',
      width: '120px',
      render: (_value: any, row: any) => (
        <div className="flex items-center gap-2 whitespace-nowrap">
          {!(row.read ?? row.is_read) ? (
            <button type="button" onClick={async () => { await notificationService.markAsRead(row.id); setNotifications((current) => current.map((item) => item.id === row.id ? { ...item, read: true, is_read: true } : item)); }} className="text-xs text-blue-600 hover:underline">Okundu</button>
          ) : null}
          {row.related_entity_type === 'waiting-record' ? (
            <button type="button" onClick={() => openReplyModal(row)} className="text-xs text-emerald-600 hover:underline inline-flex items-center gap-1">Yanıtla</button>
          ) : null}
          <button type="button" onClick={() => void handleDelete(row.id)} className="text-xs text-red-600 hover:underline inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" />Sil</button>
        </div>
      ),
    },
  ], [handleDelete, notifications]);

  return (
    <div className="workiom-screen">
      <div className="workiom-toolbar">
        <div className="workiom-toolbar-left">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Bildirimler</h1>
            <p className="mt-1 text-gray-500 dark:text-slate-400">Tüm bildirimlerinizi görüntüleyin ve yönetin</p>
          </div>
        </div>
        <div className="workiom-toolbar-right">
          <button type="button" onClick={handleMarkAllRead} className="workiom-outline-button">
            <CheckCheck className="w-4 h-4" /> Tümünü Okundu İşaretle
          </button>
          <button type="button" onClick={() => void handleDeleteAll()} className="workiom-outline-button text-rose-700 border-rose-200 hover:bg-rose-50">
            <Trash2 className="w-4 h-4" /> Tümünü Sil
          </button>
          <button type="button" onClick={() => setShowRead((c) => !c)} className={`workiom-outline-button ${!showRead ? '!bg-blue-50 !border-blue-500 !text-blue-700' : ''}`}>
            {showRead ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            {showRead ? 'Tümü' : 'Okunmamış'}
          </button>
        </div>
      </div>

      <div className="workiom-toolbar !justify-start !mx-6 !mt-0 !mb-0 rounded-t-xl border border-b-0 border-gray-200">
        <div className="flex flex-wrap items-center gap-3 w-full">
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="rounded-lg border border-gray-300 px-4 py-2">
            <option value="all">Tüm Türler</option>
            {uniqueTypes.map((t) => <option key={t} value={t}>{typeLabels[t] || t}</option>)}
          </select>
          <select value={filterRelatedEntity} onChange={(e) => setFilterRelatedEntity(e.target.value)} className="rounded-lg border border-gray-300 px-4 py-2">
            <option value="all">Alt Tür (Tümü)</option>
            <option value="waiting-record">Tahliye Problemi</option>
            <option value="weighbridge-point">Kantar Uyarısı</option>
            {uniqueRelatedEntities.filter((t) => t !== 'waiting-record' && t !== 'weighbridge-point').map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-500">Bildirimler yükleniyor...</span>
        </div>
      ) : (
        <div className="mx-6 mb-6 rounded-b-xl border border-gray-200 border-t-0 overflow-hidden">
          <AdvancedTable
            data={tableData as any}
            columns={columns as any}
            onRefresh={() => void loadNotifications()}
            getRowClassName={(record: any) => typeRowColors[String(record.type || 'system')] || ''}
          />
        </div>
      )}

      {replyingNotification ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4">
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-950">
            <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4 dark:border-slate-800">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Tahliye Bildirimine Yanıt</h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{replyingNotification.shipment_no || '-'} • {replyingNotification.driver_name || '-'}</p>
              </div>
              <button type="button" onClick={closeReplyModal} disabled={sendingReply} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50 dark:hover:bg-slate-900">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4 px-5 py-4">
              <div className="rounded-xl bg-rose-50 p-4 text-sm text-slate-700 dark:bg-rose-950/30 dark:text-slate-200">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-rose-700 dark:text-rose-300">Sürücü Bildirimi</div>
                <div className="whitespace-pre-wrap">{replyingNotification.message || '-'}</div>
              </div>

              <div className="grid gap-4 md:grid-cols-[180px_minmax(0,1fr)]">
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">Ticket Durumu</label>
                  <select value={replyStatus} onChange={(e) => setReplyStatus(e.target.value as 'in-progress' | 'resolved')} className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
                    <option value="in-progress">İşlemde</option>
                    <option value="resolved">Çözüldü</option>
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">Yanıt</label>
                  <textarea
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    rows={5}
                    placeholder="Sürücüye gönderilecek yanıtı yazın"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-4 dark:border-slate-800">
              <button type="button" onClick={closeReplyModal} disabled={sendingReply} className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
                Vazgeç
              </button>
              <button type="button" onClick={() => void handleSendReply()} disabled={sendingReply} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
                {sendingReply ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendHorizonal className="h-4 w-4" />}
                Yanıt Gönder
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
